import React, { useEffect, useRef, useState } from 'react';
import './SpaceBackground.css';

export const SPACE_VARIANTS = ['swim', 'dance', 'martian', 'coffee', 'gym'];

export function chooseSpaceVariant(random = Math.random) {
  return SPACE_VARIANTS[Math.floor(random() * SPACE_VARIANTS.length)];
}

// Deterministic positions keep the sky stable across renders.
const stars = Array.from({ length: 230 }, (_, index) => ({
  left: `${((index * 73.31 + 11.7) % 100).toFixed(2)}%`,
  top: `${((index * index * 13.17 + index * 37.9 + 4.3) % 100).toFixed(2)}%`,
  '--star-size': `${index % 11 === 0 ? 3 : index % 3 === 0 ? 2 : 1}px`,
  '--star-travel-x': `${(((index * 73.31 + 11.7) % 100) - 50).toFixed(2)}vw`,
  '--star-travel-y': `${(((index * index * 13.17 + index * 37.9 + 4.3) % 100) - 50).toFixed(2)}vh`,
  '--star-flight-duration': `${75 + (index % 8) * 5}s`,
  '--star-flight-delay': `${-((index * 0.61803398875) % 1) * (75 + (index % 8) * 5)}s`,
}));

// Opposite edges and a central waypoint keep every route inside the viewport.
export function createFlightPath(previousEdge = -1) {
  const edge = previousEdge < 0 ? Math.floor(Math.random() * 4)
    : (previousEdge + 1 + Math.floor(Math.random() * 3)) % 4;
  const point = (side) => {
    const along = 20 + Math.random() * 60;
    return [
      ['calc(-1 * var(--ufo-size))', `${along}vh`],
      ['100vw', `${along}vh`],
      [`${along}vw`, 'calc(-1 * var(--ufo-size))'],
      [`${along}vw`, '100vh'],
    ][side];
  };
  const [sx, sy] = point(edge);
  const [ex, ey] = point(edge ^ 1);
  return { edge, style: {
    '--ufo-start-x': sx, '--ufo-start-y': sy,
    '--ufo-mid-x': `${25 + Math.random() * 40}vw`,
    '--ufo-mid-y': `${25 + Math.random() * 40}vh`,
    '--ufo-end-x': ex, '--ufo-end-y': ey,
  } };
}

export default function SpaceBackground({ chinese = false }) {
  const [flight, setFlight] = useState(null);
  const [motionAllowed, setMotionAllowed] = useState(false);
  const hasArrived = useRef(false);
  const previousEdge = useRef(-1);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => {
      const allowed = !reducedMotion.matches && !document.hidden;
      setMotionAllowed(allowed);
      if (!allowed) {
        setFlight(null);
        hasArrived.current = false;
      }
    };
    updateMotion();
    reducedMotion.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateMotion);
    return () => {
      reducedMotion.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateMotion);
    };
  }, []);

  useEffect(() => {
    if (!motionAllowed) return undefined;
    // Recover even when the browser never delivers animationend (e.g. CSS cancellation).
    if (flight) {
      const watchdog = setTimeout(() => setFlight(null), 22500);
      return () => clearTimeout(watchdog);
    }
    const timer = setTimeout(() => {
      hasArrived.current = true;
      const next = createFlightPath(previousEdge.current);
      previousEdge.current = next.edge;
      setFlight({ ...next, variant: chooseSpaceVariant() });
    }, hasArrived.current ? 30000 : 2000);
    return () => clearTimeout(timer);
  }, [motionAllowed, flight]);

  return (
    <div className={`space-background${chinese ? ' space-background--chinese' : ''}`} aria-hidden="true" data-motion-paused={!motionAllowed}>
      {!chinese && stars.map((style, index) => (
        <span
          className={`space-background__star${index % 11 === 0 ? ' space-background__star--warm' : index % 7 === 0 ? ' space-background__star--blue' : ''}`}
          style={style}
          key={index}
        />
      ))}
      {flight && (
        <img
          className="space-background__ufo"
          style={flight.style}
          data-entry-edge={flight.edge}
          data-variant={flight.variant}
          src={chinese ? '/static/images/chinese-mascot/chibi-v2.gif' : `/static/images/kirikan-space/${flight.variant}.gif`}
          alt=""
          width="280"
          height="280"
          draggable="false"
          onAnimationEnd={() => setFlight(null)}
          onError={() => setFlight(null)}
        />
      )}
    </div>
  );
}
